'use client';

import { useEffect, type CSSProperties } from 'react';
import { ErrorFallbackContent, useChunkRecovery } from '@/components/ErrorFallback';
import { reportRumError } from '@/lib/datadog/rum';

/**
 * Root error boundary. Catches errors thrown in the root layout itself (which
 * app/error.tsx cannot), so it must render its own <html>/<body>. Same
 * deploy-skew auto-reload as app/error.tsx.
 *
 * This replaces the root layout, so the design-system CSS variables may be
 * absent — ErrorFallbackContent's `var(--token, fallback)` values then render
 * with their literal fallbacks.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const chunkError = useChunkRecovery(error);
  useEffect(() => {
    reportRumError(error);
  }, [error]);

  return (
    <html lang="en">
      <body style={body}>
        <ErrorFallbackContent chunkError={chunkError} onReset={reset} />
      </body>
    </html>
  );
}

const body: CSSProperties = {
  margin: 0,
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};
